<script setup lang="ts">
import { ref, watch } from 'vue';
import VExpansionPanel from '@/shared/ui/VExpansionPanel.vue';
import { useFormValidation } from '@/shared/lib/validation';
import { useUpdateApplicationMemo } from '../model/useUpdateApplicationMemo';
import { applicationMemoSchema, MEMO_MAX_LENGTH } from '../model/schema';

const props = defineProps<{
    /** 申込ID */
    applicationId: string;
    /** 現在の管理メモ */
    memo?: string | null;
}>();

const emit = defineEmits<{
    /** 保存成功時に更新後のメモを通知 */
    updated: [memo: string | null];
}>();

const { update, isLoading, error, clearError } = useUpdateApplicationMemo();

// 表示用の現在値（保存成功時に楽観的更新する）
const currentMemo = ref(props.memo ?? '');
// 親からの再取得等でmemoが変わったら同期する
watch(
    () => props.memo,
    (value) => {
        currentMemo.value = value ?? '';
    }
);

const isEditing = ref(false);

// バリデーションは Zod スキーマ（model/schema.ts）に集約する
const { form, errors, validate } = useFormValidation(applicationMemoSchema, { memo: currentMemo.value });

const startEdit = () => {
    form.value = { memo: currentMemo.value };
    errors.value = {};
    clearError();
    isEditing.value = true;
};

const cancel = () => {
    isEditing.value = false;
    errors.value = {};
    clearError();
};

const save = async () => {
    if (!validate()) return;

    const value = (form.value.memo ?? '').trim() === '' ? null : (form.value.memo ?? null);
    const success = await update(props.applicationId, value);
    if (success) {
        currentMemo.value = value ?? '';
        isEditing.value = false;
        emit('updated', value);
    }
};
</script>

<template>
    <VExpansionPanel title="管理メモ" :default-open="true">
        <div class="memo-panel-content">
            <!-- 更新エラー表示 -->
            <v-alert v-if="error" type="error" variant="tonal" class="mb-3" density="compact" closable>
                {{ error }}
            </v-alert>

            <!-- 編集モード -->
            <template v-if="isEditing">
                <v-textarea
                    v-model="form.memo"
                    variant="outlined"
                    density="compact"
                    rows="4"
                    auto-grow
                    :counter="MEMO_MAX_LENGTH"
                    placeholder="申込に関する連絡事項やメモを入力してください"
                    :error-messages="errors.memo"
                    :hide-details="!errors.memo"
                />
                <div class="d-flex ga-2 justify-end mt-3">
                    <v-btn color="primary" variant="flat" size="small" :loading="isLoading" @click="save"> 保存 </v-btn>
                    <v-btn color="grey" variant="outlined" size="small" :disabled="isLoading" @click="cancel">
                        キャンセル
                    </v-btn>
                </div>
            </template>

            <!-- 表示モード -->
            <template v-else>
                <p v-if="currentMemo" class="memo-text">{{ currentMemo }}</p>
                <p v-else class="memo-empty">メモはありません</p>
                <div class="d-flex justify-end mt-3">
                    <v-btn color="primary" variant="outlined" size="small" @click="startEdit"> 編集 </v-btn>
                </div>
            </template>
        </div>
    </VExpansionPanel>
</template>

<style scoped>
.memo-panel-content {
    padding: 16px;
    background-color: #fafafa;
}

.memo-text {
    font-size: 14px;
    color: #333;
    line-height: 1.6;
    white-space: pre-wrap;
    word-break: break-word;
}

.memo-empty {
    font-size: 14px;
    color: #999;
}
</style>
